import { useUpdateLayoutEffect } from 'ahooks';
import React from 'react';
import { CSSTransition } from 'react-transition-group';
import { Box } from '@apia/theme';
import focus from '../../../../classes/focusController';
import { isChild } from '../../../../utils/dom';
import EventEmitter from '../../../../utils/util/eventEmitter';
import { IconButton } from '../../../common';
import { icons } from '../../../common/Icon';
import Renderer from './Renderer';

export const menuEmitter = new (class MenuEmitter extends EventEmitter<{
  close: boolean;
  open: boolean;
}> {})();

export function closeMainMenu() {
  menuEmitter.emit('close', true);
}

export function openMainMenu() {
  menuEmitter.emit('open', true);
}

let timeout: ReturnType<typeof setTimeout>;

const Menu = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isMenuShown, setIsMenuShown] = React.useState(false);

  React.useEffect(() => {
    clearTimeout(timeout);
    if (isOpen) setIsMenuShown(true);
    else {
      timeout = setTimeout(() => {
        setIsMenuShown(false);
      }, 150);
    }
  }, [isOpen]);

  useUpdateLayoutEffect(() => {
    if (!isMenuShown) focus.restore();
  }, [isMenuShown]);

  React.useEffect(() => {
    const handleKey = (ev: KeyboardEvent) => {
      if (
        ev.code === 'Escape' &&
        [...document.querySelectorAll('.overlay')].length === 0
      )
        setIsOpen(false);
      if (ev.code === 'F2') setIsOpen(true);
    };
    const closeMenuByClick = (ev: MouseEvent) => {
      if (
        !isChild(
          ev.target as HTMLElement,
          (current) =>
            current.classList.contains('mainMenu__animate') ||
            current.classList.contains('hamburguerButton') ||
            current.id === 'AppModals',
        )
      )
        setIsOpen(false);
    };

    const unsuscribeOpen = menuEmitter.on('open', () => {
      setIsOpen(true);
    });
    const unsuscribeClose = menuEmitter.on('close', () => setIsOpen(false));
    document.addEventListener('keydown', handleKey);
    document.addEventListener('click', closeMenuByClick);

    return () => {
      document.removeEventListener('keydown', handleKey);
      unsuscribeOpen();
      unsuscribeClose();
      document.removeEventListener('click', closeMenuByClick);
    };
  }, []);

  const ref = React.useRef<HTMLElement>(null);

  return (
    <>
      <IconButton
        icon={icons.Hamburguer}
        {...window.getVariant('icon-primary')}
        className="hamburguerButton"
        data-test-id="main-menu"
        aria-label={window.LBL_OPEN_MAIN_MENU}
        title={window.LBL_OPEN_MAIN_MENU}
        onClick={React.useCallback(() => {
          setIsOpen((o) => !o);
        }, [])}
      />
      <CSSTransition
        in={isOpen}
        timeout={150}
        classNames="mainMenu"
        onEntered={React.useCallback(() => {
          const searchInput = ref.current?.querySelector(
            '.menu__search',
          ) as HTMLInputElement;
          searchInput?.focus();
          searchInput?.setSelectionRange(0, searchInput.value.length);
        }, [])}
      >
        <Box
          className={`mainMenu__animate ${isMenuShown ? 'shown' : 'hidden'}`}
          {...window.getVariant('layout.common.modals.mainApp.menu')}
          ref={ref}
        >
          <Renderer />
        </Box>
      </CSSTransition>
    </>
  );
};

export default Menu;
